/**
 * PaywallModal — Pro purchase flow.
 *
 * Three states:
 *   - "needs_signin" : viewer has no email-bound account → bounce them to
 *                      SignInModal. We don't try to do passwordless +
 *                      checkout in one step; the email is needed by Gumroad.
 *   - "upgrade"      : viewer is signed in but not Pro → show price + CTA.
 *                      Click → POST /api/billing/checkout → window.location
 *                      to checkoutUrl. Webhook will flip them to Pro async.
 *   - "manage"       : viewer is already Pro (lifetime) → show purchase
 *                      date, "View receipts" portal button, and the 7-day
 *                      refund button if within window. No cancel button —
 *                      Curvle Pro is a one-time lifetime unlock, nothing
 *                      to cancel.
 *
 * Pricing display reads from /api/billing/status so the WC-special vs
 * regular flip on Jul 20 2026 is server-driven (no client config to
 * forget to update).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type BillingStatus } from "@/api/client";
import { useAuthStore } from "@/store/authStore";

interface PaywallModalProps {
  open: boolean;
  onClose: () => void;
  /** When true, after successful sign-in trigger another paywall open. */
  onRequestSignIn?: () => void;
}

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function PaywallModal({ open, onClose, onRequestSignIn }: PaywallModalProps) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const FEATURES_I18N: Array<{ icon: string; title: string; body: string }> = [
    {
      icon: "🎯",
      title: t("paywall.feature_practice_title"),
      body: t("paywall.feature_practice_body"),
    },
    {
      icon: "📚",
      title: t("paywall.feature_archive_title"),
      body: t("paywall.feature_archive_body"),
    },
    {
      icon: "🏆",
      title: t("paywall.feature_leagues_title"),
      body: t("paywall.feature_leagues_body"),
    },
    {
      icon: "🛡️",
      title: t("paywall.feature_streak_title"),
      body: t("paywall.feature_streak_body"),
    },
    {
      icon: "📊",
      title: t("paywall.feature_stats_title"),
      body: t("paywall.feature_stats_body"),
    },
    {
      icon: "🎟️",
      title: t("paywall.feature_bracket_title"),
      body: t("paywall.feature_bracket_body"),
    },
  ];

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    if (!user?.email) {
      setStatus(null);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .billingStatus()
      .then((s) => setStatus(s))
      .catch(() => setError("Could not load pricing."))
      .finally(() => setLoading(false));
  }, [open, user?.email]);

  if (!open) return null;

  // ---- State 1: needs sign-in ----
  if (!user?.email) {
    return (
      <Shell title={t("paywall.signin_required_title")} onClose={onClose}>
        <p className="mt-2 text-sm opacity-80">
          {t("paywall.signin_required_body")}
        </p>
        <button
          type="button"
          className="mt-5 w-full rounded-full bg-amber-300 px-5 py-2.5 font-semibold text-emerald-950 transition hover:bg-amber-200"
          onClick={() => {
            onClose();
            onRequestSignIn?.();
          }}
        >
          {t("paywall.signin_button")}
        </button>
      </Shell>
    );
  }

  // ---- State 3: already Pro (lifetime) ----
  if (user.isPro) {
    const purchasedOn = formatDate(user.proSince);

    const proSinceMs = user.proSince ? new Date(user.proSince).getTime() : null;
    const withinRefundWindow =
      proSinceMs !== null &&
      Date.now() - proSinceMs < 7 * 24 * 60 * 60 * 1000;

    // No onCancel — Curvle Pro is a one-time lifetime unlock, there's
    // nothing to cancel. The 7-day refund button below is the only path
    // back out of Pro.

    const onRefund = async () => {
      if (busy) return;
      const ok = window.confirm(
        "Request a full refund? Your Pro access ends immediately. " +
          "The refund posts to your card in 5-10 business days.",
      );
      if (!ok) return;
      setBusy(true);
      setError(null);
      try {
        await api.requestRefund();
        await useAuthStore.getState().loadMe();
        onClose();
      } catch (err) {
        if (err instanceof ApiError && err.status === 422) {
          setError(
            "Your purchase is outside the 7-day auto-refund window or " +
              "has had a prior refund. Email vbraju@thoughtplanet.com and " +
              "we'll review your request directly.",
          );
        } else {
          setError(describeError(err));
        }
      } finally {
        setBusy(false);
      }
    };

    return (
      <Shell title={t("paywall.title")} onClose={onClose}>
        <div className="mt-3 rounded-2xl bg-amber-300/10 p-4 ring-1 ring-amber-300/30">
          <p className="text-sm">
            {user.founderNumber != null ? (
              <>
                <span className="font-semibold text-amber-200">
                  {t("founder.paywall_self_badge", { number: user.founderNumber })}
                </span>{" "}
                {t("founder.paywall_self_subtitle")}
              </>
            ) : (
              <>
                <span className="font-semibold text-amber-200">{t("paywall.pro_active")}</span>{" "}
                {t("paywall.pro_since")} <strong>{purchasedOn}</strong>.
              </>
            )}
          </p>
        </div>

        {status?.paymentProvider === "gumroad" && (
          <p className="mt-4 w-full rounded-full bg-white/5 px-5 py-2 text-center text-xs opacity-70">
            Your receipt &amp; invoice are in your email from Gumroad.
          </p>
        )}

        {status?.paymentProvider === "gumroad" && withinRefundWindow && (
          <button
            type="button"
            disabled={busy}
            className="mt-2 w-full rounded-full px-5 py-2 text-xs underline opacity-60 hover:opacity-100 disabled:opacity-30"
            onClick={onRefund}
          >
            {t("paywall.request_refund")}
          </button>
        )}

        {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}
      </Shell>
    );
  }

  // ---- State 2: signed in, not Pro → upgrade flow ----
  //
  // Gumroad is the sole purchase path as of 2026-06-02. The server builds a
  // prefilled Gumroad product URL (Gumroad has no checkout-session API) and
  // the client redirects to it; the webhook flips the user to Pro async.
  // Audience is global football fans — no region-based ordering needed.
  const onUpgrade = async (provider: "gumroad") => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { checkoutUrl } = await api.startCheckout(provider);
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(describeError(err));
      setBusy(false);
    }
  };

  const priceLabel = status ? formatPrice(status.activePriceCents) : "—";
  const isWcSpecial = status?.activeTier === "wc_special";

  return (
    <Shell title={t("paywall.title")} onClose={onClose}>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-3xl font-bold text-amber-300">{priceLabel}</span>
        <span className="text-sm opacity-70">{t("paywall.price_suffix")}</span>
      </div>
      {isWcSpecial && (
        <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-amber-200">
          {t("paywall.wc_launch_pricing")}
        </p>
      )}

      <ul className="mt-4 space-y-2.5">
        {FEATURES_I18N.map((f) => (
          <li key={f.title} className="flex items-start gap-3">
            <span aria-hidden="true" className="text-lg leading-6">{f.icon}</span>
            <div>
              <p className="text-sm font-semibold">{f.title}</p>
              <p className="text-xs opacity-70">{f.body}</p>
            </div>
          </li>
        ))}
      </ul>

      {/* Single CTA. Dodo is the sole purchase provider surfaced in UI
        globally. Stripe scaffolding remains in the server, so re-adding a
        second button here is a small change if/when Stripe approval lands. */}
      <div className="mt-5 flex flex-col gap-2">
        <CheckoutButton
          primary
          priceLabel={priceLabel}
          busy={busy || loading}
          onClick={() => onUpgrade("gumroad")}
        />
      </div>

      <p className="mt-3 text-center text-[10px] leading-tight opacity-50">
        {t("paywall.footer_secure")}
        <br />
        {t("paywall.footer_refund")}
      </p>

      {error && <p className="mt-3 text-sm text-rose-300">{error}</p>}
    </Shell>
  );
}

function Shell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
      onClick={onClose}
    >
      <div
        className="w-[min(26rem,92%)] max-h-[92vh] overflow-y-auto rounded-3xl bg-emerald-950/95 p-6 text-white shadow-2xl ring-1 ring-amber-300/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 id="paywall-title" className="text-xl font-bold">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            className="opacity-60 hover:opacity-100"
            onClick={onClose}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * CheckoutButton — the upgrade CTA in the paywall dialog. Single button now
 * that Dodo is the only purchase provider surfaced in UI. `primary` is kept
 * as a prop so callers can re-add a secondary checkout option (e.g. Stripe)
 * with a one-line addition if/when that provider gets re-enabled in UI.
 */
function CheckoutButton({
  primary,
  priceLabel,
  busy,
  onClick,
}: {
  primary?: boolean;
  priceLabel: string;
  busy: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const label = t("paywall.cta_pay_gumroad");
  const sublabel = "Card · PayPal · Apple Pay · Google Pay";
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      className={
        primary
          ? "group flex w-full flex-col items-start gap-0.5 rounded-2xl bg-amber-300 px-5 py-3 text-left text-emerald-950 transition hover:bg-amber-200 disabled:opacity-50"
          : "group flex w-full flex-col items-start gap-0.5 rounded-2xl bg-white/10 px-5 py-2.5 text-left ring-1 ring-white/15 transition hover:bg-white/20 disabled:opacity-50"
      }
    >
      <span className={primary ? "text-sm font-bold" : "text-sm font-semibold"}>
        {busy ? t("paywall.opening_checkout") : label}
        <span className={primary ? "ml-2 text-xs opacity-60" : "ml-2 text-xs opacity-50"}>
          {priceLabel} · lifetime
        </span>
      </span>
      <span className={primary ? "text-[11px] opacity-65" : "text-[11px] opacity-55"}>
        {sublabel}
      </span>
    </button>
  );
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return "Please sign in first.";
    if (err.status === 402) return "This requires Curvle Pro.";
    if (err.status === 409) return "You're already Pro.";
    if (err.status === 429) return "Too many requests — try again in a minute.";
    return err.message || "Something went wrong.";
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}
